import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, Server, ArrowRight, CheckCircle, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import SEO from '../components/SEO';
import ContactFormModal from '../components/ContactFormModal';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  // Embla Carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);
  
  // Structured Data for homepage SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "SKANDA IT SOLUTIONS",
    "alternateName": "Skanda IT Solutions",
    "description": "Leading IT Training Institute in Narasaraopeta offering Full Stack Web Development, AWS Cloud, Data Science courses with 100% placement assistance",
    "url": "https://www.skandaitsolutions.com",
    "logo": "https://www.skandaitsolutions.com/skanda-logo.png",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Narasaraopeta",
      "addressRegion": "Andhra Pradesh",
      "addressCountry": "IN"
    },
    "areaServed": {
      "@type": "State",
      "name": "Andhra Pradesh"
    }
  };

  // FAQ Schema for better indexing
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What courses does SKANDA IT SOLUTIONS offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SKANDA IT SOLUTIONS offers comprehensive IT training courses including Full Stack Web Development, AWS Cloud Computing, Data Science with Python, Java Programming, and various other professional technology courses with 100% placement assistance."
        }
      },
      {
        "@type": "Question",
        "name": "Where is SKANDA IT SOLUTIONS located?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SKANDA IT SOLUTIONS is located in Narasaraopeta, Andhra Pradesh at D.No: 11-10-38/2, Ramireddypet, Arundelpet. We serve students across Andhra Pradesh and offer both classroom and online training options."
        }
      },
      {
        "@type": "Question",
        "name": "Does SKANDA IT SOLUTIONS provide placement assistance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, SKANDA IT SOLUTIONS provides 100% placement assistance including resume building, interview preparation, mock interviews, and direct connections with top IT companies. Our placement rate is over 95%."
        }
      },
      {
        "@type": "Question",
        "name": "What makes SKANDA IT SOLUTIONS the best IT training institute in Narasaraopeta?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SKANDA IT SOLUTIONS stands out with experienced faculty, industry-aligned curriculum, hands-on practical training, modern infrastructure, 100% placement assistance, and a proven track record of successfully placing over 500+ students in top IT companies."
        }
      }
    ]
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.skandaitsolutions.com/"
      }
    ]
  };

  useEffect(() => {
    fetchTestimonials();
    fetchStats();
    fetchCompanyInfo();
    fetchHeroSlides();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/company-info`);
      // Add cache busting to logo URL
      const data = response.data;
      if (data.logo_url && !data.logo_url.includes('?v=')) {
        data.logo_url = `${data.logo_url}?v=${Date.now()}`;
      }
      setCompanyInfo(data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data.items || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats([
        { label: 'Students Trained', value: '500+' },
        { label: 'Placement Rate', value: '95%' },
        { label: 'Corporate Clients', value: '200+' },
        { label: 'Years Experience', value: '10+' },
      ]);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setTestimonials([
        {
          id: '1',
          name: 'Rahul Sharma',
          role: 'Software Engineer',
          company: 'Tech Corp',
          message: 'The IT training program was exceptional. Landed my dream job within 2 months of completion!',
          rating: 5,
        },
        {
          id: '2',
          name: 'Priya Desai',
          role: 'Network Administrator',
          company: 'InfoSys Solutions',
          message: 'Outstanding networking solutions for our enterprise. Professional team and excellent support.',
          rating: 5,
        },
        {
          id: '3',
          name: 'Amit Kumar',
          role: 'Fresh Graduate',
          company: 'Placed at Microsoft',
          message: 'Their placement assistance is top-notch. Got placed in a leading MNC with an excellent package.',
          rating: 5,
        },
      ]);
    }
  };

  const fetchHeroSlides = async () => {
    try {
      const response = await axios.get(`${API}/hero-slides`);
      setHeroSlides(response.data);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
      // Fallback to hardcoded slides if API fails
      setHeroSlides([
        {
          id: '1',
          title: 'Transform Your IT Career',
          subtitle: 'Expert Training Programs with 95% Placement Success',
          button_text: 'Explore Courses',
          button_link: '/courses',
          image_url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=2070',
          text_color: '#ffffff',
          bg_color_start: '#1e3a8a',
          overlay_opacity: 70
        },
        {
          id: '2',
          title: 'Enterprise IT Solutions',
          subtitle: 'Complete Networking & Hardware Setup for Your Business',
          button_text: 'View Solutions',
          button_link: '/services',
          image_url: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=2070',
          text_color: '#ffffff',
          bg_color_start: '#3b82f6',
          overlay_opacity: 65
        }
      ]);
    }
  };

  const services = [
    {
      icon: <GraduationCap size={32} />,
      title: 'IT Trainings',
      description: 'Industry-aligned curriculum with practical exposure and expert trainers for career success.',
      link: '/services',
    },
    {
      icon: <Users size={32} />,
      title: 'Placements & Career Support',
      description: 'Comprehensive placement assistance, interview prep, and job opportunities with top companies.',
      link: '/services',
    },
    {
      icon: <Server size={32} />,
      title: 'IT Solutions',
      description: 'Complete hardware sales, installation, networking, and email setup for enterprises.',
      link: '/services',
    },
  ];

  const features = [
    'Industry Expert Trainers',
    'Hands-on Practical Training',
    '100% Placement Assistance',
    'Latest Technology Stack',
    'Corporate-Ready Skills',
    'Lifetime Support',
  ];

  return (
    <div data-testid="home-page" className="pt-20">
      <SEO 
        title="Best IT Training Institute in Narasaraopeta"
        description="SKANDA IT SOLUTIONS is the leading IT training institute in Narasaraopeta, Andhra Pradesh. We offer courses in Full Stack Web Development, AWS Cloud, Data Science, Python with 100% placement assistance. Join the best computer training center today!"
        keywords="IT training Narasaraopeta, best computer institute Andhra Pradesh, web development course, AWS training, Data Science course, Python training, Java course, software training, placement assistance, IT solutions, SKANDA IT SOLUTIONS, coding classes Narasaraopeta"
        canonicalUrl="https://www.skandaitsolutions.com/"
      />
      
      {/* Structured Data for better indexing */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <meta name="google-site-verification" content="pending" />
        <link rel="preconnect" href="https://www.skandaitsolutions.com" />
      </Helmet>

      {/* Hero Carousel Section */}
      <section className="relative overflow-hidden">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container flex">
            {heroSlides.map((slide) => (
              <div key={slide.id} className="embla__slide flex-[0_0_100%] min-w-0 relative">
                <div className="relative h-[600px] md:h-[700px]">
                  {/* Background Image with Overlay */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                      background: slide.image_url 
                        ? `url(${slide.image_url}) center/cover` 
                        : `linear-gradient(135deg, ${slide.bg_color || '#1e3a8a'} 0%, ${slide.bg_color || '#3b82f6'} 100%)`
                    }}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(to right, rgba(15, 23, 42, ${(slide.overlay_opacity || 70) / 100}), rgba(15, 23, 42, ${(slide.overlay_opacity || 50) / 100}))`
                      }}
                    ></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative h-full flex items-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                      <div className="max-w-3xl">
                        <div className="mb-6 inline-block">
                          <span className="px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-blue-200 text-sm font-semibold">
                            {companyInfo?.company_name || 'SKANDA IT SOLUTIONS'}
                          </span>
                        </div>
                        <h1 
                          data-testid={`hero-slide-${slide.id}-title`}
                          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
                          style={{ 
                            fontFamily: 'Manrope, sans-serif',
                            color: slide.text_color || '#ffffff'
                          }}
                        >
                          {slide.title}
                        </h1>
                        <p 
                          className="text-xl md:text-2xl mb-8 leading-relaxed"
                          style={{ color: slide.text_color || '#e2e8f0' }}
                        >
                          {slide.subtitle}
                        </p>
                        <Link to={slide.button_link || '/courses'}>
                          <button 
                            data-testid={`hero-slide-${slide.id}-cta`}
                            className="group px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-sm transition-all duration-300 hover:shadow-2xl hover:scale-105 flex items-center gap-3"
                          >
                            {slide.button_text || 'Learn More'}
                            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={scrollPrev}
          data-testid="carousel-prev"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
          aria-label="Previous slide"
        >
          <ChevronLeft size={24} className="text-white" />
        </button>
        <button
          onClick={scrollNext}
          data-testid="carousel-next"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
          aria-label="Next slide"
        >
          <ChevronRight size={24} className="text-white" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi && emblaApi.scrollTo(index)}
              data-testid={`carousel-dot-${index}`}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === selectedIndex
                  ? 'bg-white w-8'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} data-testid={`stat-${index}`} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-container">
        <div className="text-center mb-16">
          <h2 data-testid="services-heading" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Our Core Services
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive IT solutions tailored for individuals, corporates, and enterprises
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} data-testid={`service-card-${index}`} className="service-card p-8 rounded-sm">
              <div className="icon-wrapper">
                {service.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {service.title}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {service.description}
              </p>
              <Link to={service.link}>
                <button data-testid={`service-learn-more-${index}`} className="flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                  Learn More <ArrowRight size={18} className="ml-2" />
                </button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 data-testid="why-choose-heading" className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Why Choose SKANDA IT?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We combine industry expertise with practical training and comprehensive support to ensure your success in the IT industry.
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} data-testid={`feature-${index}`} className="flex items-center space-x-3">
                    <CheckCircle size={24} className="text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/why-choose-us">
                <button data-testid="why-choose-learn-more" className="cta-button mt-8">
                  Learn More
                </button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758518729240-7162d07427b8?auto=format&fit=crop&q=80"
                alt="Team Meeting"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="section-container">
          <div className="text-center mb-16">
            <h2 data-testid="testimonials-heading" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-600">
              Success stories from our students and corporate partners
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={testimonial.id} data-testid={`testimonial-${index}`} className="testimonial-card">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed italic">
                  "{testimonial.message}"
                </p>
                <div>
                  <div className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-slate-500">
                    {testimonial.role}
                    {testimonial.company && ` at ${testimonial.company}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-sky-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 data-testid="cta-heading" className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Transform Your IT Career?
          </h2>
          <p className="text-xl mb-10 text-blue-50 max-w-2xl mx-auto">
            Get in touch with us today for a free consultation and discover how we can help you achieve your goals.
          </p>
          <button 
            data-testid="cta-contact-button" 
            onClick={() => setIsContactModalOpen(true)}
            className="bg-white text-blue-600 px-10 py-4 rounded-sm font-bold text-lg hover:bg-slate-100 transition-all duration-300 hover:shadow-xl"
          >
            Contact Us Now
          </button>
        </div>
      </section>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}